import React from 'react';
import { Navigate } from 'react-router-dom';
import type { CatalogImportMode } from '@/pages/catalogImport/store';
import { ResultStepView } from './ResultStepView';
import { useResultStep, type UseResultStepOptions } from './useResultStep';

export type ResultStepProps = UseResultStepOptions & {
    /** Where the step goes when it opens without an import: Products. */
    backPath: string;
    /**
     * Offers Undo: in the wizard, while onboarding is open. The in-shell
     * import page runs after onboarding, when the server refuses every undo.
     */
    showUndo: boolean;
    /**
     * Names the way on: Next step (Done) in the wizard, Back to Branches on
     * the in-shell page. `nextPath` says where it goes.
     */
    mode: CatalogImportMode;
};

/**
 * Result: the completed import's figures and what comes next. The wizard
 * mounts it at `/onboarding/result`; the in-shell import page mounts it with
 * the branch fixed and no Undo. Renders inside a `CatalogImportProvider`.
 *
 * The figures come from the store's record, so a step that opens without
 * one (the store lives in memory, and a reload empties it) goes back to
 * Products, which goes on back as it needs. That is decided once, as the
 * step opens: when Import another file or an undo drops the record, the step
 * is already on its way out and renders nothing.
 */
export const ResultStep: React.FC<ResultStepProps> = ({
    backPath,
    showUndo,
    mode,
    ...paths
}) => {
    const { record, ...step } = useResultStep(paths);
    const [openedWithoutRecord] = React.useState(record === null);

    if (openedWithoutRecord) {
        return <Navigate replace to={backPath} />;
    }
    if (record === null) {
        return null;
    }

    return (
        <ResultStepView
            mode={mode}
            record={record}
            showUndo={showUndo}
            {...step}
        />
    );
};
